import { decideVaccinationAlert, isAcknowledgedForCycle, } from "../domain/vaccinations/vaccination-alert.js";
import { formatCalendarDayUTC } from "../domain/vaccinations/vaccination-interval.js";
/**
 * The daily due-date alerting scan (`docs/products/stable.md`: "A daily
 * scheduled worker scans active horses and emits an escalating sequence").
 * Exactly what `vaccination-alert.ts`'s own doc comment describes: "the
 * daily scan use case is a thin loop around this function plus I/O" — every
 * decision is made by the pure `decideVaccinationAlert`; this function only
 * fetches candidates, calls it, and persists the result.
 *
 * Runs one alerting-enabled vaccine type at a time, across every Stable —
 * `listVaccinationAlertCandidates` is intentionally not scoped to one Stable
 * (see its own doc comment in `contracts.ts`), matching how a single
 * `scheduled-worker` invocation is meant to sweep the whole system in one
 * pass rather than being invoked once per Stable.
 *
 * Deployment shape: this is real, tested application code; the actual
 * EventBridge Scheduler wiring that would invoke it daily from a
 * `scheduled-worker` Lambda is out of scope for this phase, mirroring ADR
 * 0011's "disabled-first" posture and `ARCHITECTURE.md` 5.2's listing of
 * `scheduled-worker` as a named-but-not-yet-built deployment unit — see
 * `docs/adr/0016-vaccinations-scan-and-scheduling-deployment-shape.md`.
 */
export async function runVaccinationAlertScan(dependencies) {
    const policy = await dependencies.policy.get();
    const now = dependencies.clock.now();
    const today = formatCalendarDayUTC(now);
    const alertingVaccineTypes = Object.values(policy.vaccineTypes).filter((config) => config.alertingEnabled);
    let evaluatedCount = 0;
    let sentCount = 0;
    let suppressedAcknowledgedCount = 0;
    for (const vaccineType of alertingVaccineTypes) {
        const candidates = await dependencies.repository.listVaccinationAlertCandidates({
            vaccineType: vaccineType.code,
            now,
        });
        for (const candidate of candidates) {
            evaluatedCount += 1;
            const acknowledged = isAcknowledgedForCycle({
                latestDoseLoggedAt: candidate.latestDoseLoggedAt,
                latestOpenScheduledEntryAt: candidate.latestOpenScheduledEntryAt,
            });
            const { decision, nextTrackedState } = decideVaccinationAlert({
                nextDue: candidate.nextDue,
                today,
                warningLeadDays: policy.vaccinationAlertWarningLeadDays,
                nudgeLeadDays: policy.vaccinationAlertNudgeLeadDays,
                acknowledged,
                tracked: candidate.tracked,
            });
            await dependencies.repository.recordVaccinationAlertScanResult({
                stableId: candidate.stableId,
                horseId: candidate.horseId,
                vaccineType: vaccineType.code,
                decision,
                nextTrackedState,
                now,
            });
            if (decision.action === "SEND")
                sentCount += 1;
            if (decision.action === "SUPPRESSED_ACKNOWLEDGED") {
                suppressedAcknowledgedCount += 1;
            }
        }
    }
    return { evaluatedCount, sentCount, suppressedAcknowledgedCount };
}
